import appConstants from "../../../../appConstants";
import {
  getMinutesfromTimeString,
  getUTCDate,
} from "../../../dataGrid/gridUtilities";
import {
  SHOW_DRIVER_EVENTS,
} from "../RouteExecution/constants";
import { format, differenceInMinutes } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { downloadBlobDataByURLs } from "../RouteExecution/DriverPopUp/StopImagesDialog/getBlobImages";
import { getDataWithQueryString } from "../../../../api/componentAPIs";
import { isDriverTimeBeforeCurrentDateAndTime } from "../RouteExecution/utils";
import { PICKUPS_GRID_FILTERS } from "../RouteExecution/Pickups/Grid/Filter/DEFAULT_constants";

const formatPhone = (phone) => {
  if (!phone) {
    return null;
  }

  let prefix = "";
  if(phone.startsWith("+")){
    prefix = phone.slice(0, -10) + " ";
    phone = phone.slice(-10)
  }

  const SPECIAL_CHAR_CHECK = /[-\/\\#,+()"]/g;
  if (!SPECIAL_CHAR_CHECK.test(phone)) {
    return phone.length === 10
      ? prefix  + phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")
      : "";
  } else {
    return prefix + phone;
  }
};

const formatNumber = (number) => {
  if (!number) {
    return "";
  }
  return new Intl.NumberFormat(appConstants.LOCALES).format(number);
}

const filterAndJoin = (fieldList, delimiter) => {
  return fieldList.filter(Boolean).join(delimiter);
};
const formatAddress = ({ address, address2, city, state, zip }) => {
  return `${filterAndJoin([address, address2, city, state], ", ")} ${
    zip ?? ""
  }`.trim();
};

const formatPickupServiceWindow = (readyTime, closeTime) => {
  return `${readyTime ?? ""} - ${closeTime ?? ""}`;
};

const getStopServiceWindowByStopType = (stopData) => {
  return stopData?.stopType === appConstants.STOP_TYPE.Delivery
    ? stopData?.deliveryWindow?.replaceAll(" ", "")
    : formatPickupServiceWindow(stopData?.readyTime, stopData?.closeTime);
};

const getPendingStopStatus = (stopData, driverActualArrivedTime = "") => {
  const serviceWindow = getStopServiceWindowByStopType(stopData);
  if (serviceWindow) {
    const maxDeliveryTime = getMinutesfromTimeString(
      serviceWindow.split("-")[1]
    );
    const eta = getMinutesfromTimeString(
      driverActualArrivedTime || stopData?.arrivalTime
    );
    if (eta > maxDeliveryTime) {
      return appConstants.STOP_STATUS.DELAYED;
    }
    return appConstants.STOP_STATUS.ONTIME;
  }
  return null;
};

const getServiceLevel = (data) => {
  const serviceLevelArr = [];
  data.isApt && serviceLevelArr.push(appConstants.SpecialNeedsStrings.APT);
  data.priority?.length && serviceLevelArr.push(...data.priority);
  return serviceLevelArr.join(",");
};
const getDeliveryStatusFromShipmentDelCode = (delCode) => {
  switch (delCode) {
    case appConstants.DELIVERY_CODE.RTN:
      return appConstants.DELIVERY_STATUS.RETURN;
    case appConstants.DELIVERY_CODE.DE:
      return appConstants.DELIVERY_STATUS.DELIVERY_EXCEPTION;
    default:
      return appConstants.DELIVERY_STATUS.COMPLETED;
  }
};

const sortStopsBySequence = (stops) => {
  return stops?.sort(
    (stop, nextStop) => stop.stopSequence - nextStop.stopSequence
  )||[];
};

const getPendingStops = (stops) => {
  return sortStopsBySequence(stops).filter((stop) => !stop.isStopCompleted);
};

const isDriverCurrentStopPresent = ({ currentStatus, stops }) => {
  return stops?.some((stop) => stop?.stopId === currentStatus?.stopId);
};
const getCurrentDriverStatus = ({ currentStatus, stops }) => {
  const stopData = getCurrentStop(currentStatus, stops);
  return stopData
    ? `${SHOW_DRIVER_EVENTS[currentStatus.status]} ${stopData.name}`
    : "";
};

const getCurrentStop = ( currentStatus, stops ) => {
  return stops?.find((stop) => stop?.stopId === currentStatus?.stopId)
}

const completedVsTotalStopsFormatter = (routeData) => {
  const routeStops = sortStopsBySequence(routeData.stops);
  return `${routeStops.filter((stop) => stop.isStopCompleted).length}/${
    routeStops.length
  }`;
};

const getTotalInTrailer = (stops, field) => {
  // Exclude incomplete pickup and completed delivery and isMovedOutshipment from the total weight, total hu, and total pieces calculation
  // Include return delivery shipment value
  return [
    ...getCompletedPickupShipment(stops),
    ...getPendingAndReturnDeliveryShipment(stops),
  ].reduce(
    (total, shipment) =>
      shipment.isShipmentMovedOut ? total : total + shipment[field],
    0
  );
};

const getCompletedPickupShipment = (stops) => {
  return stops.reduce((shipmentList, stop) => {
    return stop.stopType === appConstants.STOP_TYPE.Pickup &&
      stop.isStopCompleted
      ? [...stop.shipments, ...shipmentList]
      : shipmentList;
  }, []);
};

const getPendingAndReturnDeliveryShipment = (stops) => {
  return stops.reduce((shipmentList, stop) => {
    return stop.stopType === appConstants.STOP_TYPE.Delivery
      ? stop.isStopCompleted
        ? [
            ...stop.shipments.filter(
              (shipment) =>
                shipment.deliveryCode === appConstants.DELIVERY_CODE.RTN
            ),
            ...shipmentList,
          ]
        : [...stop.shipments, ...shipmentList]
      : shipmentList;
  }, []);
};

const getTotalReturnDeliveryShipments = (stopsList) => {
  const deliveryStops = stopsList.filter(
    (stop) => stop.stopType === appConstants.STOP_TYPE.Delivery
  );
  return deliveryStops.reduce(
    (acc, stop) =>
      acc + stop.shipments.filter((shipment) => shipment.returnCode).length,
    0
  );
};

const getTimeDifferenceInMinutes = (startDate, endDate) => {
  return Math.round((endDate.getTime() - startDate.getTime()) / 60000);
};

const convertMinutesintoHours = (minutes) => {
  if (minutes >= 60) {
    return Math.floor(minutes / 60) + " hr " + (minutes % 60) + " mins";
  } else {
    return minutes + " mins";
  }
};

// This function calculates total delivery stops shipments count
const getTotalDeliveryShipments = (stops) => {
  const deliveryStops = stops.filter(
    (stop) => stop.stopType === appConstants.STOP_TYPE.Delivery
  );
  return deliveryStops.reduce((acc, stop) => acc + stop.shipments.length, 0);
};

// This function calculates pending delivery stops shipments count
const getPendingDeliveryShipments = (stopsList) => {
  const deliveryStops = stopsList.filter(
    (stop) =>
      stop.stopType === appConstants.STOP_TYPE.Delivery && !stop.isStopCompleted
  );
  return deliveryStops.reduce((acc, stop) => acc + stop.shipments.length, 0);
};

// This function calculates completed delivery stops shipments count
const getCompletedDeliveryShipments = (stopsList) => {
  const deliveryStops = stopsList.filter(
    (stop) =>
      stop.stopType === appConstants.STOP_TYPE.Delivery && stop.isStopCompleted
  );
  return deliveryStops.reduce(
    (acc, stop) =>
      acc + stop.shipments.filter((shipment) => !shipment.returnCode).length,
    0
  );
};

// This function calculate total delivery stops count
const getTotalDeliveryStops = (stops) => {
  return stops.reduce((acc, stop) => {
    if (stop.stopType === appConstants.STOP_TYPE.Delivery) {
      return acc + 1;
    }
    return acc;
  }, 0);
};

// This function calculates the completed delivery stops count
const getTotalCompletedDeliveryStops = (stopsList) => {
  return stopsList.reduce((acc, stop) => {
    if (
      stop.stopType === appConstants.STOP_TYPE.Delivery &&
      stop.isStopCompleted
    ) {
      return acc + 1;
    }
    return acc;
  }, 0);
};

// This function calculates No Freight pickup count
const getNoFreightpickupsCount = (stopsList) => {
  return stopsList.filter((stop) => stop.isNoFreight).length;
};

const isValidEmail = (email) => {
  return /^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$/.test(
    email
  );
};

const isValidPhone = (phone) => {
  return /^[+]?(\d{1,2})?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3,4}[\s.-]?\d{3,4}$/.test(
    phone
  );
};

const intersectTwo = (array1, array2) => {
  return array1.filter((x) => {
    return array2.some((y) => {
      return Object.is(x, y);
    });
  });
};


// this method provides UTC datetime with respect to given offset value.
const getTerminalDateWithOffset = (dateString, offset) => {
  const date = new Date(dateString);
  const [hours, mins] = offset.split(":").map(Number);
  const totalOffsetMins = hours * 60 + (hours < 0 ? -1 : 1) * mins;
  return format(
    new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes() - totalOffsetMins,
      date.getUTCSeconds()
    ),
    appConstants.DATETYPES.DATETIME.updateFormat
  );
};



// this method provides the datetime in respective time zone using the given offset value.
const getTerminalDateByOffset = (dateString, offset) => {
  if(offset){
    const date = new Date(dateString);
    const [hours, mins] = offset.split(":").map(Number);
    const totalOffsetMins = hours * 60 + (hours < 0 ? -1 : 1) * mins;
    return format(
      new Date(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes() + totalOffsetMins,
        date.getUTCSeconds()
      ),
      appConstants.DATETYPES.DATETIMEWITHOUTTIMEZONE.format
    );
  }
  return ""
};

const getDiffInMinutesWithTerminalTime = (date, terminalOffset) => {
  const origDate = new Date(date);
  const today = utcToZonedTime(new Date(), terminalOffset);
  const timeDiff = getTimeDifferenceInMinutes(
    new Date(0, 0, 0, origDate.getHours(), origDate.getMinutes()),
    new Date(0, 0, 0, today.getHours(), today.getMinutes())
  );
  return timeDiff;

  /* In this function we are comparing  date param which should be coming in terminal 
    timezone , to current time of the terminal timezone and returning the difference in minutes. 
  */
};

const getServiceWindow = (params) => {
  const { stopType, deliveryWindow, readyTime, closeTime } = params.data;
  if (stopType === appConstants.STOP_TYPE.Pickup) {
    return formatPickupServiceWindow(readyTime, closeTime);
  }
  return deliveryWindow;
};

const getFilteredLunchBreakDelays = (delays) => {
  return delays?.filter((delay) => delay.type === appConstants.DELAY_STOP_TYPES.LUNCH_BREAK) || [];
}

const getFilteredLunchBreakDelaysByDriverRoute = (delays, driverRouteIds) => {
  // filter delayed lunch stop only for currently dispatched drivers routes.
  // this function will filter out previously dispatched routes's delays 
  return (
    delays?.filter(
      (delay) =>
        delay.type === appConstants.DELAY_STOP_TYPES.LUNCH_BREAK &&
        driverRouteIds.includes(delay.routeId)
    ) || []
  );
};

const getTotalLunchTimeInMinutes = (lunchBreakStops, terminalOffset) => {
  const currentTime = utcToZonedTime(new Date(), terminalOffset)
  return lunchBreakStops.reduce((acc,delayStop)=>{
    const arrivalTime = delayStop.startTime;
    const deptTime = delayStop.endTime ?? format(currentTime,"HH:mm"); 
    const arrivalTimeInMinutes = getMinutesfromTimeString(arrivalTime);
    const departureTimeInMinutes = getMinutesfromTimeString(deptTime);
    const timeDiff = departureTimeInMinutes - arrivalTimeInMinutes;
    return acc + timeDiff;
  },0)
}

const getDailyAccruedHours = ({
  driverStartTime,
  lunchBreakStops,
  terminalOffset
}) => {
  if(!driverStartTime){
    return 0;
  }
  const currentTime = getUTCDate(new Date());
  const clockInTime = getUTCDate(driverStartTime);
  const currentAndClockedInTimeDiff = differenceInMinutes(
    currentTime,
    clockInTime
  );
  const totalLunchTime = getTotalLunchTimeInMinutes(lunchBreakStops, terminalOffset);
  const totalAccTimeInMinutes = currentAndClockedInTimeDiff - totalLunchTime;
  const dailyAccruedHours = parseFloat((totalAccTimeInMinutes / 60).toFixed(2));
  return dailyAccruedHours > 0 ? dailyAccruedHours: 0;
};

const getOverTimeHours = (actualHOS, allowedHOS) => {
  if(!allowedHOS){
    return 0;
  }
  return actualHOS - allowedHOS > 0
    ? parseFloat((actualHOS - allowedHOS).toFixed(2))
    : 0;
};

const getWeeklyAccruedHours = (dailyAccruedHours, accruedHours = 0) => {
  return parseFloat((accruedHours + dailyAccruedHours).toFixed(2));
};

const getAllCompletedStops = (stops) => {
  return sortStopsBySequence(stops).filter(stop => stop.isStopCompleted)
}

const getLastCompletedPickupStop = (stops) => {
  return getAllCompletedStops(stops).filter(
    (stop) => stop.stopType === appConstants.STOP_TYPE.Pickup
  ).at(-1);
}
const getUpdatedColumnHeaderName = ({ column, gridShortHeaderNamesConfig }) => {
  return {
    ...column,
    defaultHeaderName: column.headerName,
    ...(gridShortHeaderNamesConfig?.[column?.field] ?? {}),
  };
};

const updateGridColumnHeaderNames = ({
  gridShortHeaderNamesConfig,
  columnConfig,
  enableColumnShortForm,
}) => {
  if(!enableColumnShortForm){
    return columnConfig;
  }
  const columns = columnConfig.columns.map((column) => {
    if (column.children?.length) {
      return {
        ...column,
        children: column.children.map((childCol) =>
          getUpdatedColumnHeaderName({
            column: childCol,
            gridShortHeaderNamesConfig,
          })
        ),
      };
    }
    return getUpdatedColumnHeaderName({ column, gridShortHeaderNamesConfig });
  });
  return {
    ...columnConfig,
    columns,
  };
};

const getDriverDispatchTimeInUtcTime = (driverDispatchTime, terminalOffset) => {
  const driverDispatchTimeInUtcFormat = format(
    driverDispatchTime,
    appConstants.DATETYPES.DATETIME.updateFormat
  );
  return getTerminalDateWithOffset(
    driverDispatchTimeInUtcFormat,
    terminalOffset
  );
}

const getDriverRouteSPHBPHCount = ({
  driverRoutes,
  driverDispatchTime,
  terminalOffset,
  lunchBreakStops
}) => {
  if (driverRoutes.length === 0 || !driverDispatchTime) {
    return { stopsPerHour: 0, billsPerHour: 0 };
  }
  const driverDispatchTimeInUtcTime = getDriverDispatchTimeInUtcTime(
    driverDispatchTime,
    terminalOffset
  );
  if (!isDriverTimeBeforeCurrentDateAndTime(driverDispatchTimeInUtcTime)) {
    return { stopsPerHour: 0, billsPerHour: 0 };
  }
  const kpiData = driverRoutes.reduce(
    (driverKpi, eachRoute) => {
      const routeKpi = eachRoute.stops.reduce(
        (routeKpi, eachStop) => {        
            if (!eachStop.isStopCompleted) {
              return routeKpi;
            }
            routeKpi.completedStops += 1;
            routeKpi.completedBills += eachStop.shipments.length;
            return routeKpi;
        },
        { completedStops: 0, completedBills: 0 }
      );
      driverKpi.completedStops += routeKpi.completedStops;
      driverKpi.completedBills += routeKpi.completedBills;
      return driverKpi;
    },
    { completedStops: 0, completedBills: 0 }
  );

  const totalManHours = getTotalManHours({
    driverDispatchTimeInUtcTime,
    lunchBreakStops,
    terminalOffset,
  });
  return {
    stopsPerHour: totalManHours
      ? (kpiData.completedStops / totalManHours).toFixed(2)
      : 0,
    billsPerHour: totalManHours
      ? (kpiData.completedBills / totalManHours).toFixed(2)
      : 0,
  };
}

const getTotalManHours = ({
  driverDispatchTimeInUtcTime,
  lunchBreakStops,
  terminalOffset,
}) => {
  const currentTime = getUTCDate(new Date());
  const dispatchTime = getUTCDate(driverDispatchTimeInUtcTime);
  const currentAndDispatchTimeDiff = differenceInMinutes(
    currentTime,
    dispatchTime
  );

  const totalLunchTime = getTotalLunchTimeInMinutes(
    lunchBreakStops,
    terminalOffset
  );
  return parseFloat(
    ((currentAndDispatchTimeDiff - totalLunchTime) / 60).toFixed(2)
  );
};


async function getImagesFromBlobStorage(imageData) {
  const allDriversImages = imageData.map((i) => i.image);
  const populatedImageData = [];
  return downloadBlobDataByURLs(allDriversImages)
    .then((blogImages) => {
      blogImages.forEach((image, index) => {
        populatedImageData.push({
          driverNumber: imageData[index].driverNumber,
          driverImage: `data:image/png;base64, ${image}`,
        });
      });
    })
    .then((_responses) => {
      return populatedImageData;
    })
    .catch((_error) => {
      return [];
    });
}

const fetchDriverImage = (driverNumbers, terminalCode) => {
  return getDataWithQueryString(
    "GetDriverImages",
    `terminalCode=${terminalCode}&driverNumbers=${JSON.stringify(
      driverNumbers
    )}`
  ).then(
    (response) => {
      if (response && response.status === 200 && response.data.length !== 0) {
        return getImagesFromBlobStorage(response.data);
      }
      return [];
    },
    (_error) => {
      return [];
    }
  );
};

const isShipmentsHasExcessiveLength = (shipments, stopType) => {
  return shipments?.some((shipment) => {
    if (stopType === appConstants.STOP_TYPE.Delivery) {
      return shipment.excessiveLength === appConstants.EXCESSIVE_SHIPMENT.YES;
    }
    return shipment.isExcessiveLength;
  });
};

const getDuplicatePickupsByPickupId = (duplicatePickups, pickupId) => {
  return duplicatePickups.find(
    (d) => d.pickupId.toLowerCase() === pickupId?.toLowerCase()
  )?.duplicatePickups ?? [];
};

const isPossibleMergeFilterApplied = (filterValue) => {
  return PICKUPS_GRID_FILTERS.possibleMerge === filterValue;
}

const getPickupStopStatus = (stop) => {
  const { PICKUP_STATUS } = appConstants;
  return stop.isNoFreight
    ? PICKUP_STATUS.NO_FREIGHT
    : PICKUP_STATUS.COMPLETED;
}

export {
  formatPhone,
  formatAddress,
  formatNumber,
  getPendingStopStatus,
  getServiceLevel,
  getServiceWindow,
  getDeliveryStatusFromShipmentDelCode,
  formatPickupServiceWindow,
  sortStopsBySequence,
  getCurrentDriverStatus,
  isDriverCurrentStopPresent,
  completedVsTotalStopsFormatter,
  getTotalInTrailer,
  getTotalReturnDeliveryShipments,
  getTimeDifferenceInMinutes,
  convertMinutesintoHours,
  getTotalDeliveryShipments,
  getPendingDeliveryShipments,
  getCompletedDeliveryShipments,
  getTotalCompletedDeliveryStops,
  getTotalDeliveryStops,
  getNoFreightpickupsCount,
  getPendingStops,
  isValidEmail,
  isValidPhone,
  getStopServiceWindowByStopType,
  intersectTwo,
  getTerminalDateWithOffset,
  getTerminalDateByOffset,
  getCurrentStop,
  getDiffInMinutesWithTerminalTime,
  getDailyAccruedHours,
  getOverTimeHours,
  getWeeklyAccruedHours,
  getLastCompletedPickupStop,
  updateGridColumnHeaderNames,
  getAllCompletedStops,
  getDriverRouteSPHBPHCount,
  fetchDriverImage,
  getFilteredLunchBreakDelays,
  isShipmentsHasExcessiveLength,
  getDuplicatePickupsByPickupId,
  isPossibleMergeFilterApplied,
  getFilteredLunchBreakDelaysByDriverRoute,
  getPickupStopStatus
};
